import { cn } from "@journal/shared";
import { AppSidebar } from "@/components/app-sidebar";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";
import { useNavDrawerSwipe } from "@/features/layout/hooks/use-nav-drawer-swipe";
import { useBackDismiss } from "@/lib/use-back-dismiss";

/**
 * Panel transform. Open and at rest it yields exactly `translate3d(0, 0, 0)`:
 * the drag only shows up in the style while there *is* a drag, so the resting
 * state stays a single comparable value.
 */
function panelTransform(isOpen: boolean, dragPx: number): string {
  if (!isOpen) {
    return "translate3d(-100%, 0, 0)";
  }
  return dragPx === 0 ? "translate3d(0, 0, 0)" : `translate3d(${dragPx}px, 0, 0)`;
}

interface AppLayoutSidebarProps {
  isOpen: () => boolean;
  setOpen: (open: boolean) => void;
}

export function AppLayoutSidebar(props: AppLayoutSidebarProps) {
  let panel: HTMLElement | undefined;

  // Browser back closes the mobile drawer instead of navigating away (PWA pattern).
  useBackDismiss(
    () => props.isOpen(),
    () => {
      props.setOpen(false);
    },
  );

  // Swipe left to dismiss; swiping right does nothing (see the hook). The
  // gesture listens on the BOX, not the panel, so dragging from the dimmed
  // backdrop works too.
  const swipe = useNavDrawerSwipe({
    isOpen: () => props.isOpen(),
    close: () => {
      props.setOpen(false);
    },
    panel: () => panel,
  });

  return (
    <>
      {/* ── Mobile Sidebar Drawer ───────────────────────────────── */}
      <div
        data-testid="mobile-nav-drawer"
        class={cn(
          "fixed inset-0 z-[100] lg:hidden overflow-hidden transition-all duration-300 ease-swift",
          props.isOpen() ? "pointer-events-auto" : "pointer-events-none",
        )}
        // `touch-action: pan-y`: the browser keeps vertical scrolling and
        // yields the horizontal axis. Without it, it competes and the drag
        // gets cut off.
        style={{ "touch-action": "pan-y" }}
        {...swipe.handlers}
      >
        {/* Backdrop */}
        <button
          type="button"
          data-testid="mobile-nav-backdrop"
          onClick={() => {
            props.setOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              props.setOpen(false);
            }
          }}
          class={cn(
            "absolute inset-0 bg-black/40 backdrop-blur-[2px] border-none p-0 w-full h-full cursor-default ease-swift",
            // While the finger is in charge the backdrop obeys the finger: the
            // transition is off and the drag sets the opacity.
            swipe.isDragging() ? "transition-none" : "transition-opacity duration-300",
            props.isOpen() ? "opacity-100" : "opacity-0",
          )}
          style={{ opacity: swipe.isDragging() ? String(swipe.backdropOpacity()) : undefined }}
          // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
          aria-label="Close sidebar"
        />
        {/* Drawer */}
        <aside
          ref={panel}
          data-testid="mobile-nav-aside"
          class={cn(
            "absolute left-0 top-0 bottom-0 flex w-[280px] flex-col border-r border-border/40 bg-card/85 backdrop-blur-md shadow-2xl ease-swift will-change-transform",
            "pl-[var(--safe-area-inset-left)] pb-[var(--safe-area-inset-bottom)]",
            // With the transition on, the panel would arrive 300ms after the
            // finger is already somewhere else.
            swipe.isDragging() ? "transition-none" : "transition-transform duration-300",
          )}
          style={{
            transform: panelTransform(props.isOpen(), swipe.dragPx()),
          }}
        >
          <AppSidebar
            onCloseSidebar={() => {
              props.setOpen(false);
            }}
          />
        </aside>
      </div>

      {/* ── Desktop Sidebar ───────────────────────────────────── */}
      {/* P23/CC-60 · I1: SHELL anchor for the `MutationObserver` probe.
          El test de identidad no sirve (los nodos vuelven), así que la sonda
          marca ESTE nodo y mide el HUECO en el que desaparece del DOM. */}
      <aside
        data-testid="app-shell-sidebar"
        class="hidden lg:flex w-64 shrink-0 flex-col border-r border-border/40 bg-card/10 backdrop-blur-md"
      >
        <AppSidebar />
      </aside>

      {/* Account switcher overlay — mounted ONCE for both sidebars so the
          `?account=switch` URL flag never opens two stacked dialogs. */}
      <AccountSwitcherHost />
    </>
  );
}
